const upper = (value, fallback = "") =>
  String(value ?? fallback).trim().toUpperCase();

export const RECONCILIATION_FILTERS = [
  "ALL",
  "PROCESSED",
  "FAILED",
  "UNMATCHED",
  "SUSPENSE",
  "MANUALLY_RECONCILED",
];

export const normalizeReconciliationTransaction = (transaction = {}, index = 0) => {
  const savvyPayment = transaction.savvy_payment || {};
  const charges = transaction.charges || {};
  const receipt = String(transaction.receipt || transaction.transaction_id || "").trim();
  const reference = String(transaction.reference || transaction.payment_reference || "").trim();
  const savvyStatus = upper(
    transaction.savvy_status || savvyPayment.status || transaction.internal_status,
    "UNSEEN",
  );
  let reconciliationStatus = upper(transaction.reconciliation_status);
  if (!reconciliationStatus) {
    if (savvyStatus === "PROCESSED") reconciliationStatus = "MATCHED";
    else if (savvyStatus === "RECEIVED" || savvyStatus === "PENDING") reconciliationStatus = "SUSPENSE";
    else reconciliationStatus = "UNMATCHED";
  }

  return {
    id: receipt || reference || String(index),
    receipt,
    reference,
    timestamp: transaction.timestamp || transaction.date || transaction.created_at || null,
    customerName:
      transaction.customer_name ||
      transaction.payer ||
      transaction.member_name ||
      "-",
    accountNumber: transaction.account_number || transaction.account || "-",
    channel: transaction.channel || transaction.method || "NMB",
    currency: transaction.currency || "TZS",
    amount: transaction.amount ?? charges.gross ?? null,
    bankConfirmed: transaction.bank_confirmed !== false,
    bankEvidenceStatus: upper(
      transaction.bank_evidence_status,
      transaction.bank_confirmed === false ? "LOCAL_ONLY" : "RECEIVED",
    ),
    savvyStatus,
    reconciliationStatus,
    allocationStatus: upper(transaction.allocation_status, "UNALLOCATED"),
    failureCategory:
      transaction.failure_category || savvyPayment.failure_category || null,
    failureReason:
      transaction.failure_reason || savvyPayment.failure_reason || null,
    allocationBreakdown: Array.isArray(
      transaction.allocation_breakdown || savvyPayment.allocation_breakdown,
    )
      ? transaction.allocation_breakdown || savvyPayment.allocation_breakdown
      : [],
    charges: {
      gross: charges.gross ?? null,
      sscRate: charges.ssc_rate ?? null,
      sscAmount: charges.ssc_amount ?? null,
      bankChargeRate: charges.bank_charge_rate ?? null,
      bankChargeAmount: charges.bank_charge_amount ?? null,
      netAllocatable: charges.net_allocatable ?? null,
    },
    savvyPayment: {
      exists: Boolean(savvyPayment.exists),
      id: savvyPayment.id ?? null,
      source: savvyPayment.source || transaction.reconciliation_source || null,
      processedAt: savvyPayment.processed_at || transaction.reconciled_at || null,
    },
    reconciledBy: transaction.reconciled_by || null,
  };
};

export const transactionMatchesStatus = (transaction, status) => {
  if (status === "ALL") return true;
  if (status === "PROCESSED") {
    return (
      transaction.savvyStatus === "PROCESSED" &&
      transaction.reconciliationStatus !== "MANUALLY_RECONCILED"
    );
  }
  if (status === "FAILED") {
    return ["FAILED", "SECURITY_FAILED"].includes(transaction.savvyStatus);
  }
  if (status === "UNMATCHED") {
    return transaction.reconciliationStatus === "UNMATCHED";
  }
  if (status === "SUSPENSE") {
    return (
      ["SUSPENSE", "NEEDS_REVIEW"].includes(transaction.reconciliationStatus) ||
      ["PENDING", "RECEIVED"].includes(transaction.savvyStatus)
    );
  }
  return transaction.reconciliationStatus === status;
};

export const filterReconciliationTransactions = (
  transactions,
  searchQuery = "",
  status = "ALL",
) => {
  const query = searchQuery.trim().toLowerCase();
  return transactions.filter((transaction) => {
    const matchesSearch =
      !query ||
      transaction.receipt.toLowerCase().includes(query) ||
      transaction.reference.toLowerCase().includes(query) ||
      transaction.customerName.toLowerCase().includes(query);
    return matchesSearch && transactionMatchesStatus(transaction, status);
  });
};

export const summarizeReconciliation = (transactions) =>
  transactions.reduce(
    (summary, transaction) => {
      summary.total += 1;
      const amount = Number(transaction.amount);
      summary.totalAmount += Number.isFinite(amount) ? amount : 0;
      if (transaction.savvyStatus === "PROCESSED") summary.processed += 1;
      if (["FAILED", "SECURITY_FAILED"].includes(transaction.savvyStatus)) {
        summary.failed += 1;
      }
      if (transaction.reconciliationStatus === "UNMATCHED") summary.unmatched += 1;
      if (
        ["SUSPENSE", "NEEDS_REVIEW"].includes(transaction.reconciliationStatus) ||
        ["PENDING", "RECEIVED"].includes(transaction.savvyStatus)
      ) {
        summary.needsReview += 1;
      }
      return summary;
    },
    {
      total: 0,
      totalAmount: 0,
      processed: 0,
      failed: 0,
      unmatched: 0,
      needsReview: 0,
    },
  );

export const canManualReconcile = (transaction) =>
  Boolean(
    transaction?.bankConfirmed &&
      transaction?.receipt &&
      transaction?.reference &&
      transaction?.savvyStatus !== "PROCESSED" &&
      (["UNMATCHED", "SUSPENSE", "NEEDS_REVIEW"].includes(
        transaction?.reconciliationStatus,
      ) ||
        ["FAILED", "SECURITY_FAILED", "PENDING", "RECEIVED", "UNSEEN"].includes(
          transaction?.savvyStatus,
        )),
  );
